import React from "react";

const HEALTH_ORDER = { red: 0, amber: 1, green: 2 };

const HealthDot = ({ health }) => {
  const colors = {
    green: "bg-emerald-400",
    amber: "bg-amber-400",
    red:   "bg-red-400",
  };
  const labels = { green: "Healthy", amber: "Watch", red: "At Risk" };
  return (
    <span className="flex items-center gap-1.5">
      <span className={`inline-block w-2.5 h-2.5 rounded-full flex-shrink-0 ${colors[health]}`} />
      <span className={`text-xs font-medium ${
        health === "green" ? "text-emerald-600" :
        health === "amber" ? "text-amber-600" : "text-red-600"
      }`}>
        {labels[health]}
      </span>
    </span>
  );
};

const fmtRev = (n) => {
  if (n == null || n === 0) return "—";
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}k`;
  return `$${n.toFixed(0)}`;
};

const fmtNum = (n, suffix = "") => (n == null ? "—" : `${n.toLocaleString()}${suffix}`);

const DifotCell = ({ pct }) => {
  if (pct == null) return <span className="text-gray-400 text-xs">No data</span>;
  const color = pct >= 85 ? "text-emerald-600" : pct >= 70 ? "text-amber-600" : "text-red-600";
  return <span className={`font-medium ${color}`}>{pct.toFixed(1)}%</span>;
};

const TicketsCell = ({ open }) => {
  if (open == null) return <span className="text-gray-400">—</span>;
  const color = open > 20 ? "text-red-600 font-semibold" : open > 8 ? "text-amber-600 font-medium" : "text-gray-700";
  return <span className={color}>{open}</span>;
};

const ResolutionCell = ({ days }) => {
  if (days == null) return <span className="text-gray-400">—</span>;
  const color = days > 7 ? "text-amber-600" : "text-gray-700";
  return <span className={color}>{days.toFixed(1)}d</span>;
};

export default function ScorecardTable({ companies, groupByCsm }) {
  const sorted = [...(companies || [])].sort(
    (a, b) => (HEALTH_ORDER[a.health] - HEALTH_ORDER[b.health]) || (b.revenue_4wk - a.revenue_4wk)
  );

  if (!sorted.length) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-10 text-center text-gray-400">
        No customers found for this filter.
      </div>
    );
  }

  // Group by CSM if requested, otherwise flat
  const groups = groupByCsm
    ? Object.entries(
        sorted.reduce((acc, c) => {
          const key = c.csm || "Unassigned";
          (acc[key] = acc[key] || []).push(c);
          return acc;
        }, {})
      )
    : [["All", sorted]];

  const headerCells = [
    { label: "Status",          cls: "w-28" },
    { label: "Parent Company",  cls: "min-w-[200px]" },
    { label: "CSM",             cls: "w-36 hidden md:table-cell" },
    { label: "Revenue (4wk)",   cls: "w-32 text-right" },
    { label: "GP%",             cls: "w-20 text-right" },
    { label: "Shipments",       cls: "w-24 text-right" },
    { label: "DIFOT% *",        cls: "w-24 text-right" },
    { label: "Open Tickets",    cls: "w-24 text-right" },
    { label: "Avg Resolution",  cls: "w-28 text-right" },
  ];

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              {headerCells.map((h) => (
                <th
                  key={h.label}
                  className={`px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide ${h.cls}`}
                >
                  {h.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {groups.map(([csm, rows]) => (
              <React.Fragment key={csm}>
                {groupByCsm && (
                  <tr className="bg-blue-50 border-t border-blue-100">
                    <td colSpan={9} className="px-4 py-2 text-xs font-bold text-blue-700 uppercase tracking-widest">
                      {csm}
                    </td>
                  </tr>
                )}
                {rows.map((c, i) => (
                  <tr
                    key={c.parent_company}
                    className={`border-b border-gray-100 hover:bg-gray-50 transition-colors ${
                      i % 2 === 0 ? "bg-white" : "bg-gray-50/40"
                    }`}
                  >
                    <td className="px-4 py-3">
                      <HealthDot health={c.health} />
                    </td>
                    <td className="px-4 py-3 font-medium text-gray-900">
                      {c.parent_company}
                    </td>
                    <td className="px-4 py-3 text-gray-500 text-xs hidden md:table-cell">
                      {c.csm}
                    </td>
                    <td className="px-4 py-3 text-right font-semibold text-gray-900">
                      {fmtRev(c.revenue_4wk)}
                    </td>
                    <td className={`px-4 py-3 text-right font-medium ${
                      c.gp_pct >= 20 ? "text-emerald-600" : c.gp_pct < 0 ? "text-red-600" : "text-gray-700"
                    }`}>
                      {c.gp_pct != null ? `${c.gp_pct.toFixed(1)}%` : "—"}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-700">
                      {fmtNum(c.shipments_4wk)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <DifotCell pct={c.difot_pct} />
                    </td>
                    <td className="px-4 py-3 text-right">
                      <TicketsCell open={c.open_tickets} />
                    </td>
                    <td className="px-4 py-3 text-right">
                      <ResolutionCell days={c.avg_resolution_days} />
                    </td>
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
